import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useLogin } from '../hooks/useLogin';
import { useSignup } from '../hooks/useSignup';
import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { validateReferralCode, getReferralCodeError } from '../utils/authValidation';
import Button from '../components/common/Button';
import '../styles/auth-animations.css';

const ModernAuthPage = () => {
  const location = useLocation();
  const isSignupInProgress = useRef(false);

  // Company logos for the grid
  const companyLogos = [
    { name: 'Airtable', src: '/img/logos/airtable-logo.jpeg' },
    { name: 'AWS', src: '/img/logos/aws-logo.png' },
    { name: 'Stripe', src: '/img/logos/stripe-logo.png' },
    { name: 'McKinsey', src: '/img/logos/mckinsey-logo.png' },
    { name: 'OpenAI', src: '/img/logos/openai-logo.jpeg' },
    { name: 'Databricks', src: '/img/logos/databricks-logo.png' },
    { name: 'J.P. Morgan', src: '/img/logos/jpmorgan-logo.png' },
    { name: 'SpaceX', src: '/img/logos/SpaceX-logo.svg' },
    { name: 'Deloitte', src: '/img/logos/deloitte-logo-.png' },
  ];

  // Get initial mode from URL params or default to login
  const getInitialMode = () => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    return mode === 'signup' ? 'signup' : 'login';
  };

  const [authMode, setAuthMode] = useState(getInitialMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Form states for login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Additional form states for signup
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralCodeError, setReferralCodeError] = useState('');

  // Progressive experience states
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [isReferralCodeValid, setIsReferralCodeValid] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Hooks
  const { login, isPending: isLoginPending, error: loginError } = useLogin();
  const { signup, isPending: isSignupPending, error: signupError } = useSignup();
  const { signInWithGoogle, isPending: isGooglePending, error: googleError } = useGoogleSignIn();

  // Handle URL changes
  useEffect(() => {
    const handlePopState = () => {
      const newMode = getInitialMode();
      if (newMode !== authMode) {
        setAuthMode(newMode);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [authMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle referral code changes and validation
  const handleReferralCodeChange = (value) => {
    setReferralCode(value);
    setReferralCodeError('');

    const isValid = validateReferralCode(value);
    setIsReferralCodeValid(isValid);

    // Smooth animation when valid code is entered
    if (isValid && !showSignupForm) {
      setIsAnimating(true);
      // Small delay to show the "Valid code!" message and success pulse
      setTimeout(() => {
        setShowSignupForm(true);
        // Animation completes after the CSS transition
        setTimeout(() => setIsAnimating(false), 800);
      }, 1000);
    } else if (!isValid && showSignupForm) {
      // Hide signup form if code becomes invalid with smooth transition
      setIsAnimating(true);
      setShowSignupForm(false);
      setTimeout(() => setIsAnimating(false), 600);
    }

    // Show error for invalid codes
    if (value && !isValid) {
      setReferralCodeError(getReferralCodeError(value));
    }
  };

  // Handle mode switching
  const switchMode = (newMode) => {
    if (newMode === authMode || isTransitioning) return;

    setIsTransitioning(true);

    // Clear referral code state when switching from signup to login
    if (authMode === 'signup' && newMode === 'login') {
      setReferralCode('');
      setReferralCodeError('');
      setIsReferralCodeValid(false);
      setShowSignupForm(false);
      setIsAnimating(false);
    }

    // Update URL without page reload
    const newUrl = `/auth${newMode === 'signup' ? '?mode=signup' : ''}`;
    window.history.pushState({}, '', newUrl);

    setTimeout(() => {
      setAuthMode(newMode);
      setIsTransitioning(false);
    }, 400);
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    await login(email, password);
    // Note: No need to manually redirect here
    // App.js routing will automatically redirect waitlist users to /waitlist
    // and regular users to their appropriate page
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();

    if (!isReferralCodeValid) {
      setReferralCodeError(getReferralCodeError(referralCode));
      return;
    }

    isSignupInProgress.current = true;
    try {
      const displayName = `${firstName.trim()} ${lastName.trim()}`;
      await signup(email, password, displayName);
    } finally {
      isSignupInProgress.current = false;
    }
  };

  // Handle Google auth
  const handleGoogleAuth = async () => {
    if (authMode === 'signup' && !isReferralCodeValid) {
      setReferralCodeError(getReferralCodeError(referralCode));
      return;
    }

    if (authMode === 'signup') {
      isSignupInProgress.current = true;
    }
    try {
      // Allow account creation only in signup mode with valid referral code
      const allowAccountCreation = authMode === 'signup' && isReferralCodeValid;
      await signInWithGoogle(null, allowAccountCreation);
      // Note: No need to manually redirect here
      // App.js routing will automatically redirect waitlist users to /waitlist
      // and regular users to their appropriate page
    } finally {
      if (authMode === 'signup') {
        isSignupInProgress.current = false;
      }
    }
  };

  const currentError = loginError || signupError || googleError;
  const isPending = isLoginPending || isSignupPending || isGooglePending;

  // Render Phase 1: Referral Code Only (for signup mode)
  const renderReferralCodeOnlyView = () => {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md referral-transition-container">
          <div className={`text-center mb-8 transition-all duration-500 ${isAnimating ? 'animate-slide-up-out' : ''}`}>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Burnt</h1>
            <p className="text-gray-600">Enter your referral code to get started</p>
          </div>

          <div className={`space-y-4 transition-all duration-500 ${isAnimating ? 'animate-slide-up-out' : ''}`}>
            <div>
              <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
                Referral code
              </label>
              <input
                type="text"
                id="referralCode"
                value={referralCode}
                onChange={(e) => handleReferralCodeChange(e.target.value)}
                className={`w-full px-6 py-4 text-lg border rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-300 shadow-sm ${referralCodeError
                  ? 'border-red-300 focus:border-red-500'
                  : isReferralCodeValid
                    ? 'border-green-300 focus:border-green-500 animate-success-pulse'
                    : 'border-gray-300 focus:border-purple-500'
                  }`}
                placeholder="Enter your referral code"
                autoFocus
                disabled={isAnimating}
              />
              {referralCodeError ? (
                <div className="mt-3 text-sm text-red-600 flex items-center animate-fadeIn">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {referralCodeError}
                </div>
              ) : isReferralCodeValid ? (
                <div className="mt-3 text-sm text-green-600 flex items-center animate-fadeIn">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {isAnimating ? 'Preparing your signup form...' : 'Valid code! Loading signup form...'}
                </div>
              ) : (
                <div className="mt-3 text-sm text-gray-500">
                  You'll need a referral code to join Burnt
                </div>
              )}
            </div>
          </div>

          <div className={`mt-8 text-center transition-all duration-500 ${isAnimating ? 'animate-slide-up-out' : ''}`}>
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => switchMode('login')}
                className="text-purple-700 hover:text-purple-800 font-medium underline transition-colors duration-200"
                disabled={isAnimating}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render Phase 2: Full Signup Form (after valid referral code)
  const renderFullSignupForm = () => {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-md referral-transition-container">
          <div className={`text-center mb-8 ${showSignupForm ? 'animate-slide-down-in' : ''}`}>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h1>
            <p className="text-gray-600">Just a few more details to get started</p>
          </div>

          {/* Verified Referral Code Display */}
          <div className={`mb-6 p-3 bg-green-50 border border-green-200 rounded-lg ${showSignupForm ? 'animate-float-in animate-float-in-1' : ''}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-green-700 font-medium">Code verified: {referralCode}</span>
              </div>
              <button
                onClick={() => {
                  setIsAnimating(true);
                  setShowSignupForm(false);
                  setTimeout(() => {
                    setReferralCode('');
                    setIsReferralCodeValid(false);
                    setIsAnimating(false);
                  }, 600);
                }}
                className="text-sm text-purple-600 hover:text-purple-700 underline transition-colors duration-200"
              >
                Change
              </button>
            </div>
          </div>

          {/* Error Message */}
          {currentError && (
            <div className={`mb-6 p-4 bg-red-50 border border-red-200 rounded-lg ${showSignupForm ? 'animate-float-in animate-float-in-1' : ''}`}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{currentError}</p>
              </div>
            </div>
          )}

          {/* Animated Form Container */}
          <div className={`transition-all duration-600 ease-out transform ${showSignupForm
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-8 scale-95 pointer-events-none'
            }`}>
            <form onSubmit={handleSignup} className="space-y-5">
              {/* Name Fields */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${showSignupForm ? 'animate-float-in animate-float-in-2' : ''}`}>
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              {/* Email Field */}
              <div className={`${showSignupForm ? 'animate-float-in animate-float-in-3' : ''}`}>
                <label htmlFor="signupEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  id="signupEmail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {/* Password Field */}
              <div className={`${showSignupForm ? 'animate-float-in animate-float-in-4' : ''}`}>
                <label htmlFor="signupPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="signupPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L7.5 7.5m2.378 2.378a3 3 0 004.243 4.243m0 0L12 12m0 0l2.121 2.121" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.923 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className={`${showSignupForm ? 'animate-float-in animate-float-in-5' : ''}`}>
                <Button
                  type="submit"
                  className="w-full py-3 mt-6 bg-purple-600 text-white hover:bg-purple-700 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                  disabled={isPending}
                >
                  {isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating account...
                    </div>
                  ) : (
                    'Create account'
                  )}
                </Button>
              </div>
            </form>

            {/* Divider */}
            <div className={`relative my-6 ${showSignupForm ? 'animate-float-in animate-float-in-5' : ''}`}>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-gradient-to-br from-purple-50 to-pink-50 px-4 text-gray-600">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <div className={`${showSignupForm ? 'animate-float-in animate-float-in-5' : ''}`}>
              <Button
                onClick={handleGoogleAuth}
                className="w-full flex items-center justify-center py-3 !bg-white border border-gray-300 hover:!bg-gray-50 !text-gray-700 font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                disabled={isPending}
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="w-5 h-5 mr-3"
                />
                <span>
                  {isGooglePending ? 'Signing up...' : 'Continue with Google'}
                </span>
              </Button>
            </div>

            {/* Footer */}
            <div className={`mt-8 text-center ${showSignupForm ? 'animate-float-in animate-float-in-5' : ''}`}>
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => switchMode('login')}
                  className="text-purple-700 hover:text-purple-800 font-medium underline transition-colors duration-200"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Login Form (with company logos)
  const renderLoginForm = () => {
    return (
      <div className="min-h-screen bg-white flex">
        {/* Left Column - Auth Forms */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 px-8 py-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
              <p className="text-gray-600">Sign in to your account to continue</p>
            </div>

            {/* Error Message */}
            {currentError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-700">{currentError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all duration-200 shadow-sm min-h-[44px]"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L7.5 7.5m2.378 2.378a3 3 0 004.243 4.243m0 0L12 12m0 0l2.121 2.121" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.923 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-purple-700 hover:text-purple-800 transition-colors duration-200 underline font-medium">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full py-3 mt-6 bg-purple-600 text-white hover:bg-purple-700 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-gradient-to-br from-purple-50 to-pink-50 px-4 text-gray-600">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <Button
              onClick={handleGoogleAuth}
              className="w-full flex items-center justify-center py-3 !bg-white border border-gray-300 hover:!bg-gray-50 !text-gray-700 font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              disabled={isPending}
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-5 h-5 mr-3"
              />
              <span>
                {isGooglePending ? 'Signing in...' : 'Continue with Google'}
              </span>
            </Button>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => switchMode('signup')}
                  className="text-purple-700 hover:text-purple-800 font-medium underline transition-colors duration-200"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - Company Logos & Social Proof */}
        <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-center bg-gray-50 px-12 py-12">
          <div className="max-w-lg text-center">
            {/* Join the Network Header */}
            <div className="mb-12">
              <h1 className="text-2xl font-semibold text-gray-800 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
                Join the network
              </h1>
            </div>

            {/* Company Logos Section */}
            <div className="mb-16">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-8">
                Trusted by professionals from
              </p>

              {/* Company Logos Grid */}
              <div className="grid grid-cols-3 gap-6 items-center justify-items-center mb-8">
                {companyLogos.slice(0, 9).map((company) => (
                  <div key={company.name} className="w-16 h-16 flex items-center justify-center p-2 bg-white rounded-xl shadow-sm border border-gray-200">
                    <img
                      src={company.src}
                      alt={company.name}
                      className={`object-contain ${['Deloitte', 'SpaceX', 'Stripe'].includes(company.name)
                        ? 'max-h-6 w-auto'
                        : 'max-h-10 w-auto'
                        }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Value Proposition */}
            <div className="space-y-4">
              <div className="flex items-center text-gray-700">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">Vetted introductions to decision-makers</span>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">Exclusive community of high-achievers</span>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm">Access to hidden opportunities</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render logic with smooth transitions
  const renderWithTransition = (content) => {
    return (
      <div className={`${isTransitioning ? 'animate-mode-slide-left' : 'animate-mode-slide-right'}`}>
        {content}
      </div>
    );
  };

  if (authMode === 'login') {
    return renderWithTransition(renderLoginForm());
  }

  // For signup mode, show referral code view first, then signup form
  if (authMode === 'signup') {
    if (!showSignupForm) {
      return renderWithTransition(renderReferralCodeOnlyView());
    } else {
      return renderWithTransition(renderFullSignupForm());
    }
  }

  // Fallback
  return renderWithTransition(renderLoginForm());
};

export default ModernAuthPage;